import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { adminAPI } from '../../services/adminApi';
import { formatMoney } from '../../utils/formatMoney';
import { LoadFailed } from '../settings/instantSettings';
import { fmtDMY, fmtNum } from '../home/format';
import { athensYmd } from '../../utils/athensDate';
import {
  ChipTone, Column, DataTable, DetailPanel, EmptyState, GHOST_BTN, KeyValue, KeyValueItem, ListPage, ListToolbar, StatusChip,
} from '../shared/listPage';

/**
 * Συμφωνία ημέρας (super admin) στη διάταξη «Πίνακας» (14/10/2026): ανά κατάστημα, για μία (περασμένη)
 * ημέρα, οι τρεις πηγές χρημάτων — εισπράξεις παραγγελιών, παραστατικά ΑΑΔΕ, εγκεκριμένες χρεώσεις POS —
 * και οι εισπραγμένες παραγγελίες χωρίς απόδειξη. Ίδιο endpoint με πριν: GET /api/admin/reconciliation?date.
 * Το κλικ σε γραμμή ανοίγει όλη την ανάλυση του καταστήματος (και ό,τι ο server ήδη στέλνει: μετρητά,
 * κάρτα, προπληρωμένα, συναλλαγές POS) — στο κινητό ο πίνακας δείχνει μόνο τα βασικά.
 */

export interface ReconRow {
  store_id: number;
  store_name: string;
  orders: number;
  orders_revenue: string | number;
  cash?: string | number;
  card: string | number;
  prepaid?: string | number;
  fiscal_docs: number;
  fiscal_gross: string | number;
  boundary_gross: string | number;
  cancellation_docs: string | number;
  pos_txns?: number;
  pos_amount: string | number;
  no_receipt_orders: string | number;
  no_receipt_amount: string | number;
  delta_orders_fiscal: string | number;
  delta_card_pos?: string | number;
  status: 'ok' | 'warn' | 'alert' | string;
}

// Τα ποσά έρχονται ως κείμενο (NUMERIC)· όπως πριν, ό,τι δεν διαβάζεται μετρά 0.
const num = (v: unknown): number => parseFloat(String(v)) || 0;
const money = (v: unknown): string => formatMoney(num(v));

/** Δ όπως πριν: εισπράξεις − παραστατικά, με τα «εκτός ημέρας» προστιθέμενα πίσω (δικαιολογημένη διαφορά). */
export const adjustedDelta = (r: Pick<ReconRow, 'delta_orders_fiscal' | 'boundary_gross'>): number =>
  parseFloat(String(r.delta_orders_fiscal)) + parseFloat(String(r.boundary_gross));

/** «−12,50» / «0,00»: με πραγματικό μείον, όπως τα υπόλοιπα ποσά της εφαρμογής (χωρίς €, είναι διαφορά). */
const fmtDelta = (v: number): string => {
  const n = Number.isFinite(v) ? v : 0;
  const abs = new Intl.NumberFormat('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n));
  return n < -0.004 ? `−${abs}` : abs;
};

const TONE: Record<string, ChipTone> = { ok: 'ok', warn: 'warn' };
const toneOf = (status: string): ChipTone => TONE[status] ?? 'danger';

// Σήμερα ΩΡΑΣ ΕΛΛΑΔΑΣ, όπως τη διαβάζει ο server. Ως 24/10/2026 ήταν η ημερομηνία UTC: από 00:00 ως 03:00
// ανοίγαμε στο χθες και το «max» δεν επέτρεπε καν το σήμερα.
const athensToday = () => athensYmd();

type ReconError = { message?: string } | null;

// Επικεφαλίδα που αναδιπλώνεται σε δύο γραμμές («Χωρίς / απόδειξη») αντί να κοπεί με «…».
const wrapHead = (text: string) => <span className="block whitespace-normal leading-4">{text}</span>;

const SuperAdminReconTab: React.FC = () => {
  const { t } = useTranslation(['superadminOps', 'common', 'listPage']);
  const [date, setDate] = useState<string>(() => athensToday());
  const [stores, setStores] = useState<ReconRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ReconError>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const seq = useRef(0);

  const load = useCallback((d: string) => {
    const id = ++seq.current;
    setLoading(true);
    setError(null);
    adminAPI.getReconciliation(d)
      // Ο interceptor του adminApi γυρίζει ήδη το σώμα της απάντησης.
      .then((r: any) => { if (id === seq.current) setStores(r?.stores || []); })
      .catch((e: any) => {
        if (id !== seq.current) return;
        setStores([]);
        setError({ message: e?.response?.data?.error || e?.message || undefined });
      })
      .finally(() => { if (id === seq.current) setLoading(false); });
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const statusLabel = useCallback((status: string) => (
    status === 'ok' ? t('superadminOps:recon.status.ok')
      : status === 'warn' ? t('superadminOps:recon.status.warn')
        : t('superadminOps:recon.status.alert')
  ), [t]);

  const chip = useCallback((r: ReconRow) => <StatusChip tone={toneOf(r.status)}>{statusLabel(r.status)}</StatusChip>, [statusLabel]);

  // Δέκα στήλες στο πλάτος του φύλλου (~1000px δίπλα στο πλαϊνό μενού): κομμάτια με ελάχιστο πλάτος που
  // μοιράζονται τον χώρο (το άθροισμα των ελάχιστων < 960, το όριο του «wide»), οι μεγάλες επικεφαλίδες
  // σε δύο γραμμές αντί για «…». Στο κινητό η γραμμή meta κρατά μόνο ό,τι κρίνει: εισπράξεις, Δ, χωρίς απόδειξη.
  const columns = useMemo<Column<ReconRow>[]>(() => [
    {
      key: 'store_name',
      header: t('superadminOps:recon.col.store'),
      width: 'minmax(112px, 1.5fr)',
      render: r => (
        <>
          <span className="block font-medium">{r.store_name}</span>
          <span className="block text-xs text-t4">#{r.store_id}</span>
        </>
      ),
      stackRender: r => r.store_name,
    },
    {
      key: 'orders',
      header: wrapHead(t('superadminOps:recon.col.orders')),
      width: 'minmax(60px, 0.65fr)',
      align: 'right',
      hideBelow: 'medium',
      render: r => <span className="text-t2">{fmtNum(num(r.orders))}</span>,
    },
    {
      key: 'orders_revenue',
      header: wrapHead(t('superadminOps:recon.col.revenue')),
      width: 'minmax(92px, 1fr)',
      align: 'right',
      render: r => <span className="font-medium">{money(r.orders_revenue)}</span>,
      stackRender: r => `${t('superadminOps:recon.col.revenue')} ${money(r.orders_revenue)}`,
    },
    {
      key: 'fiscal_gross',
      header: wrapHead(t('superadminOps:recon.col.fiscal')),
      width: 'minmax(92px, 1fr)',
      align: 'right',
      stack: 'none',
      render: r => (
        <>
          <span className="block text-t2">{money(r.fiscal_gross)}</span>
          <span className="block text-xs text-t4" title={t('superadminOps:recon.docs', { count: num(r.fiscal_docs) })}>({fmtNum(num(r.fiscal_docs))})</span>
        </>
      ),
    },
    {
      key: 'boundary_gross',
      header: wrapHead(t('superadminOps:recon.col.boundary')),
      width: 'minmax(76px, 0.9fr)',
      align: 'right',
      hideBelow: 'medium',
      render: r => (num(r.boundary_gross) > 0 ? <span className="text-t3">{money(r.boundary_gross)}</span> : <span className="text-t4">—</span>),
    },
    {
      key: 'delta',
      header: <span title={t('superadminOps:recon.deltaTitle')}>{t('superadminOps:recon.col.delta')}</span>,
      width: 'minmax(60px, 0.75fr)',
      align: 'right',
      render: r => {
        const d = adjustedDelta(r);
        return Math.abs(d) > 0.05
          ? <span className="font-semibold text-red-700 dark:text-red-400">{fmtDelta(d)}</span>
          : <span className="text-t4">{fmtDelta(d)}</span>;
      },
      stackRender: r => `${t('superadminOps:recon.col.delta')} ${fmtDelta(adjustedDelta(r))}`,
    },
    {
      key: 'card_pos',
      header: wrapHead(t('superadminOps:recon.col.cardPos')),
      width: 'minmax(92px, 1fr)',
      align: 'right',
      hideBelow: 'medium',
      render: r => (
        <>
          <span className="block text-t2">{money(r.card)}</span>
          <span className="block text-xs text-t3">{t('superadminOps:recon.pos', { amount: money(r.pos_amount) })}</span>
        </>
      ),
    },
    {
      key: 'cancellation_docs',
      header: wrapHead(t('superadminOps:recon.col.cancellations')),
      width: 'minmax(68px, 0.8fr)',
      align: 'right',
      hideBelow: 'medium',
      render: r => (parseInt(String(r.cancellation_docs), 10) > 0
        ? <span className="text-amber-700 dark:text-amber-400">{r.cancellation_docs}</span>
        : <span className="text-t4">—</span>),
    },
    {
      key: 'no_receipt',
      header: wrapHead(t('superadminOps:recon.col.noReceipt')),
      width: 'minmax(92px, 1fr)',
      align: 'right',
      render: r => {
        const count = parseInt(String(r.no_receipt_orders), 10);
        if (!(count > 0)) return <span className="text-t4">—</span>;
        const danger = num(r.no_receipt_amount) > 0;
        // Πρώτα το ποσό (αυτό κρίνει), από κάτω μικρό το πλήθος — χωρά σε δύο γραμμές στη στενή στήλη.
        return (
          <span className={danger ? 'text-red-700 dark:text-red-400' : 'text-t2'}>
            <span className="block font-semibold">{money(r.no_receipt_amount)}</span>
            <span className="block text-xs">{t('superadminOps:recon.orders', { count })}</span>
          </span>
        );
      },
      stackRender: r => (parseInt(String(r.no_receipt_orders), 10) > 0
        ? t('superadminOps:recon.noReceiptStack', { count: parseInt(String(r.no_receipt_orders), 10), amount: money(r.no_receipt_amount) })
        : ''),
    },
    {
      key: 'status',
      header: t('superadminOps:recon.col.status'),
      width: 'minmax(96px, 1fr)',
      align: 'center',
      stack: 'end',
      render: chip,
    },
  ], [t, chip]);

  const detailItems = (r: ReconRow): KeyValueItem[] => {
    const d = adjustedDelta(r);
    const noReceipt = parseInt(String(r.no_receipt_orders), 10);
    const cancellations = parseInt(String(r.cancellation_docs), 10);
    return [
      { id: 'status', label: t('superadminOps:recon.col.status'), value: chip(r) },
      { id: 'orders', label: t('superadminOps:recon.detail.orders'), value: fmtNum(num(r.orders)) },
      {
        id: 'revenue',
        label: t('superadminOps:recon.col.revenue'),
        value: money(r.orders_revenue),
        hint: r.cash !== undefined || r.prepaid !== undefined
          ? t('superadminOps:recon.detail.revenueSplit', { cash: money(r.cash), card: money(r.card), prepaid: money(r.prepaid) })
          : undefined,
      },
      { id: 'fiscal', label: t('superadminOps:recon.col.fiscal'), value: money(r.fiscal_gross), hint: t('superadminOps:recon.docs', { count: num(r.fiscal_docs) }) },
      {
        id: 'boundary',
        label: t('superadminOps:recon.col.boundary'),
        value: num(r.boundary_gross) > 0 ? money(r.boundary_gross) : null,
        hint: t('superadminOps:recon.detail.boundaryHint'),
      },
      {
        id: 'delta',
        label: t('superadminOps:recon.detail.delta'),
        value: <span className={Math.abs(d) > 0.05 ? 'font-semibold text-red-700 dark:text-red-400' : undefined}>{fmtDelta(d)}</span>,
        hint: t('superadminOps:recon.deltaTitle'),
      },
      {
        id: 'card',
        label: t('superadminOps:recon.detail.card'),
        value: money(r.card),
      },
      {
        id: 'pos',
        label: t('superadminOps:recon.detail.pos'),
        value: money(r.pos_amount),
        hint: r.pos_txns !== undefined ? t('superadminOps:recon.detail.posTxns', { count: num(r.pos_txns) }) : undefined,
      },
      { id: 'cancellations', label: t('superadminOps:recon.col.cancellations'), value: cancellations > 0 ? cancellations : null },
      {
        id: 'noReceipt',
        label: t('superadminOps:recon.col.noReceipt'),
        value: noReceipt > 0 ? (
          <span className={num(r.no_receipt_amount) > 0 ? 'font-semibold text-red-700 dark:text-red-400' : undefined}>
            {t('superadminOps:recon.noReceiptShort', { count: noReceipt, amount: money(r.no_receipt_amount) })}
          </span>
        ) : null,
        hint: noReceipt > 0 ? t('superadminOps:recon.detail.noReceiptHint') : undefined,
      },
    ];
  };

  const selected = selectedId !== null ? stores.find(s => s.store_id === selectedId) ?? null : null;
  const alerts = stores.filter(s => s.status !== 'ok' && s.status !== 'warn').length;
  const warnings = stores.filter(s => s.status === 'warn').length;
  const summary = !loading && !error
    ? [
      t('superadminOps:recon.count', { count: stores.length }),
      ...(alerts > 0 ? [t('superadminOps:recon.alerts', { count: alerts })] : []),
      ...(warnings > 0 ? [t('superadminOps:recon.warnings', { count: warnings })] : []),
    ].join(' · ')
    : null;

  return (
    <ListPage
      eyebrow={t('superAdminPanel:nav.groups.finance')}
      title={t('superadminOps:recon.title')}
      subtitle={t('superadminOps:recon.subtitle')}
      testId="sa-recon"
    >
      <ListToolbar
        filters={(
          <input
            type="date"
            className="lp-date"
            style={{ height: 44 }}
            value={date}
            max={athensToday()}
            onChange={e => e.target.value && setDate(e.target.value)}
            aria-label={t('superadminOps:recon.day')}
            title={t('superadminOps:recon.day')}
            data-testid="sa-recon-date"
          />
        )}
        actions={(
          <button type="button" className={GHOST_BTN} onClick={() => load(date)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('superadminOps:refresh')}
          </button>
        )}
        summary={summary}
      />

      <p className="mb-4 max-w-[920px] text-[13px] leading-5 text-t3" data-testid="sa-recon-legend">
        {t('superadminOps:recon.legend')}{' '}
        <span className="font-medium text-red-700 dark:text-red-400">{t('superadminOps:recon.legendCheck')}</span>.
      </p>

      {error ? (
        <LoadFailed
          message={error.message ? t('superadminOps:recon.loadFailedWith', { error: error.message }) : t('superadminOps:recon.loadFailed')}
          onRetry={() => load(date)}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={stores}
          rowKey={r => r.store_id}
          onRowClick={r => setSelectedId(r.store_id)}
          selectedKey={selectedId}
          loading={loading}
          stickyHeader
          rowLabel={r => t('superadminOps:recon.rowLabel', { store: r.store_name, status: statusLabel(r.status) })}
          empty={<EmptyState title={t('superadminOps:recon.empty')} hint={t('superadminOps:recon.emptyHint', { date: fmtDMY(date) })} />}
          testId="sa-recon-table"
        />
      )}

      <DetailPanel
        open={selected !== null}
        onClose={() => setSelectedId(null)}
        title={selected ? selected.store_name : ''}
        subtitle={selected ? t('superadminOps:recon.detail.subtitle', { id: selected.store_id, date: fmtDMY(date) }) : undefined}
        footer={<button type="button" className={GHOST_BTN} onClick={() => setSelectedId(null)}>{t('listPage:close')}</button>}
        testId="sa-recon-detail"
      >
        {selected && <KeyValue items={detailItems(selected)} testId="sa-recon-values" />}
      </DetailPanel>
    </ListPage>
  );
};

export default SuperAdminReconTab;
